import React, {useEffect, useMemo, useState} from 'react';
import {Divider, Form, Input, Space} from 'antd';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {connect} from 'react-redux';
import {
  StyledExpenseDetails,
  StyledExpenseDetailsButton,
  StyledExpenseDetailsCollapse,
} from './StyledExpenseDetails';
import UserDetails from '../../../../../UserDetails';
import CompanyTagSelect from '../../../../TransactionsPage/CompanyTagSelect/CompanyTagSelect';
import DetailsTable from '../../../../TransactionsPage/DetailsTable';
import {expensesHelpers} from '../../../../../../utils/helpers';
import {ExternalLinkIcon, StarsIcon} from '../../../../../../icons';
import {expenseAgentTypeConstants, tagFamilyConstants} from '../../../../../../constants';
import {expenseActions} from '../../../../../../state/actions/expense.actions';

const formId = 'expense-details-form';

const ExpenseDetails = ({
  dispatch,
  employeeEmail,
  openExpenseAgentChat,

  availableLink = false,
  expense,
  onEdit,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'expenses']);
  const [initialFormValues, setInitialFormValues] = useState({
    category_tags: [],
    description: '',
    location_tags: [],
    project_tags: [],
    tags: [],
    team_tags: [],
  });
  const [form] = Form.useForm();

  const enableMissingInfoButton = useMemo(() => {
    const isExpenseReportedByEmployee = expense?.reported_by?.email === employeeEmail;
    const isExpenseMissingInfo = expensesHelpers.isExpenseMissingInfo(expense);
    return isExpenseReportedByEmployee && isExpenseMissingInfo;
  }, [employeeEmail, expense]);

  const title = useMemo(() => {
    let title = `${t('expense')} ${t('details')}`;
    if (!availableLink) return title;
    return (
      <Space size='small' align='center'>
        <span>{title}</span>
        <Link to={expensesHelpers.getExpenseDetailsUrl(expense)}>
          <ExternalLinkIcon />
        </Link>
      </Space>
    );
  }, [availableLink, expense, t]);

  const agentsSummary = expense?.approval_summary || '';

  useEffect(() => {
    if (expense) {
      const initialValues = {
        category_tags: expense?.category_tags || [],
        description: expense?.description || '',
        location_tags: expense?.location_tags || [],
        project_tags: expense?.project_tags || [],
        tags: expense?.tags || [],
        team_tags: expense?.team_tags || [],
      };
      setInitialFormValues(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [expense]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTags = (tagName, tagFamily) => {
    const tags = initialFormValues?.[tagName] || [];
    return (
      <CompanyTagSelect
        autoLimit={true}
        className='expense-details-tags-select'
        onChange={(value) => handleEdit({[tagName]: value})}
        selected={tags}
        tagFamily={tagFamily}
        variant='button'
      />
    );
  }

  const statusLabel = expensesHelpers.getExpenseStatusLabel(expense, t);

  const handleSubmitForm = () => form.submit();

  const handlePressEnter = () => {
    handleSubmitForm();
    document.querySelectorAll(`#${formId} input`).forEach((input) => input.blur());
  }

  const tableData = [
    {
      key: 'paidBy',
      label: t('paidBy'),
      value: expense?.reported_by && (
        <UserDetails
          email={false}
          style={{ display: 'flex', flexDirection: 'row-reverse' }}
          user={expense.reported_by}
        />
      )
    },
    {
      key: 'amount',
      label: t('amount'),
      value: expensesHelpers.getExpensePrice(expense)
    },
    {
      key: 'description',
      label: t('description'),
      value: (
        <Form.Item name='description'>
          <Input.TextArea autoSize={{minRows: 1}} onBlur={handleSubmitForm} onPressEnter={handlePressEnter} />
        </Form.Item>
      )
    },
    {
      key: 'vendor',
      label: t('vendor'),
      value: expense?.vendor ? expensesHelpers.getExpenseVendorNameWithLink(expense, 'system') : '-'
    },
    {
      key: 'expenseType',
      label: `${t('expense')} ${t('type')}`,
      value: expensesHelpers.getExpenseTypeInfo(expense)
    },
    {
      key: 'costCenter',
      label: t('costCenter'),
      value: getTags('tags', tagFamilyConstants.COST_CENTER)
    },
    {
      key: 'expenseCategory',
      label: t('expenseCategory'),
      value: getTags('category_tags', tagFamilyConstants.EXPENSE)
    },
    {
      key: 'teamTags',
      label: t('team'),
      value: getTags('team_tags', tagFamilyConstants.TEAM)
    },
    {
      key: 'projectTags',
      label: t('project'),
      value: getTags('project_tags', tagFamilyConstants.PROJECT)
    },
    {
      key: 'locationTags',
      label: t('location'),
      value: getTags('location_tags', tagFamilyConstants.LOCATION)
    }
  ];

  const handleEdit = (fields) => onEdit?.(fields);

  const handleOpenAgentChat = () => {
    openExpenseAgentChat({variant: expenseAgentTypeConstants.FILL_MISSING_INFO, query: { expense_id: expense?.id }});
  }

  return (
    <StyledExpenseDetails
      extra={statusLabel}
      title={title}
      type='inner'
      {...rest}
    >
      {enableMissingInfoButton && (
        <StyledExpenseDetailsButton
          icon={<StarsIcon />}
          onClick={handleOpenAgentChat}
          size='large'
          type='primary'
        >
          {t('fillMissingInfo')}
        </StyledExpenseDetailsButton>
      )}
      {agentsSummary && (
        <>
          <StyledExpenseDetailsCollapse defaultActiveKey={['1']} ghost>
            <StyledExpenseDetailsCollapse.Panel
              header={<Space size={4}><StarsIcon /><span>{t('agentsSummary')}</span></Space>}
              key='1'
            >
              {expense?.approval_summary || ''}
            </StyledExpenseDetailsCollapse.Panel>
          </StyledExpenseDetailsCollapse>
          <Divider />
        </>
      )}
      <Form
        id={formId}
        initialValues={initialFormValues}
        form={form}
        onFinish={handleEdit}
        requiredMark={false}
      >
        <DetailsTable data={tableData} />
      </Form>
    </StyledExpenseDetails>
  );
}

ExpenseDetails.propTypes = {
  availableLink: PropTypes.bool,
  expense: PropTypes.object,
  onEdit: PropTypes.func,
}

const mapStateToProps = ({user: {employee}}) => ({
  employeeEmail: employee.email,
});

const mapDispatchToProps = {
  openExpenseAgentChat: expenseActions.openExpenseAgentChat
}

export default connect(mapStateToProps, mapDispatchToProps)(ExpenseDetails);
